/**
 * localStorage helper for the last-used tenant slug. Persists across
 * sessions so the post-login landing can resume the operator's most
 * recent workspace without forcing a tenant selection on every visit.
 *
 * **Why localStorage, not sessionStorage:** the resume behavior should
 * survive browser restarts — operators expect "I was on Acme yesterday,
 * I'm on Acme today." sessionStorage would re-show the picker each
 * morning.
 *
 * **Multi-tab safety:** writes are flat-file; reading from one tab while
 * another writes is fine (last-write-wins). Operators with multiple
 * tabs open on different tenants see whichever was last clicked. The
 * URL is still the canonical state per-tab — localStorage only matters
 * for the *initial* landing at `/`.
 *
 * **Validation deferred to caller:** the slug stored here may no longer
 * be in the operator's tenants[] (e.g. access was revoked between
 * sessions). RootLanding checks against the current `tenants` list and
 * falls back to the selector when the stored slug is stale.
 */

const STORAGE_KEY = 'messaging-front:lastTenantSlug';

export function getLastTenantSlug(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can throw in private-browsing modes / restricted contexts.
    // Treat as "no memory" and fall through to the picker.
    return null;
  }
}

export function setLastTenantSlug(slug: string): void {
  if (!slug) return;
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Fail-silent — losing this preference is fine; just slightly worse UX
    // on next visit. Don't surface the error.
  }
}

export function clearLastTenantSlug(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
